import React, { useEffect, useRef } from "react";
import { Button } from "@progress/kendo-react-buttons";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { requiredValidator } from "../../components/formFields/CommonValidator";
import FormTextField from "../../components/formFields/FormTextField";
import ButtonWithLoading from "../../components/common/ButtonWithLoading";
import { useAppDispatch, useAppSelector } from "../../app/hooks";
import { LoadingPanel } from "../../components/layout/Loading";
import { clearStateDetails } from "./stateSlice";
import FormSelectionField from "../../components/formFields/FormSelectionField";
import {
  createState,
  updateState,
  getAllStates,
  getStateByID,
} from "./services/state.services";
import { getAllActiveCountries } from "../country/services/country.services";
import { closed } from "../../components/muidrawer/muiDrawerSlice";
import { Typography } from "@progress/kendo-react-common";
import { RxCross1 } from "react-icons/rx";
import { STATUSARRAY } from "../../_contstants/common";
import IconButton from "../../components/common/IconButton";

const CreateState: React.FC = () => {
  const dispatch = useAppDispatch();
  const gridRef = useRef<any>(null);

  const CountryList = useAppSelector((state) => state.country.CountryList);
  const StateDetail = useAppSelector((state) => state.state.StateDetail);
  const StateID = useAppSelector((state) => state.muidrawer.data);
  const formLoading = useAppSelector((state) => state.state.formLoading);
  const [formKey, setFormKey] = React.useState(0);

  useEffect(() => {
    dispatch(getAllActiveCountries());

    return () => {
      dispatch(clearStateDetails());
      dispatch(closed());
    };
  }, []);

  useEffect(() => {
    setFormKey(formKey + 1);
  }, [StateDetail]);

  useEffect(() => {
    if (StateID) {
      dispatch(getStateByID(StateID));
    }
  }, [StateID]);

  const handleSubmit = async (values: any) => {
    try {
      if (!StateID) {
        const insertPayload = {
          StateName: values.StateName,
          CountryID: values.CountryID,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(createState(insertPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearStateDetails());
          dispatch(getAllStates());
        }
      } else {
        const editPayload = {
          ID: StateID,
          StateName: values.StateName,
          CountryID: values.CountryID,
          IsActive: values.IsActive === 1,
        };
        const response = await dispatch(updateState(editPayload));
        if (response?.meta?.requestStatus === "fulfilled") {
          dispatch(closed());
          dispatch(clearStateDetails());
          dispatch(getAllStates());
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      throw error;
    }
  };

  const handleFormClose = () => {
    dispatch(closed());
  };

  return (
    <>
      {formLoading && <LoadingPanel gridRef={gridRef} />}
      <Form
        key={formKey}
        onSubmit={handleSubmit}
        initialValues={StateDetail}
        render={(formRenderProps: FormRenderProps) => (
          <FormElement style={{ width: "100%" }}>
            <GridLayout
              style={{
                display: "flex",
                flexWrap: "wrap",
              }}
              gap={{ rows: 10 }}
              cols={[{ width: "100%" }]}
            >
              <GridLayoutItem
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  margin: "17px 0 8px",
                }}
              >
                <Typography.h4 className="m-0">
                  {StateID ? "Update State" : "Add State"}
                </Typography.h4>
                <IconButton
                  type="button"
                  fillMode={"flat"}
                  themeColor={"dark"}
                  style={{ marginRight: 4 }}
                  onClick={() => dispatch(closed())}
                >
                  <RxCross1 />
                </IconButton>
              </GridLayoutItem>

              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  name="StateName"
                  label="State Name"
                  placeholder="i.e. Gujarat"
                  astrike={true}
                  maxLength={50}
                  component={FormTextField}
                  validator={requiredValidator}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"CountryID"}
                  name={"CountryID"}
                  label={"Country Name"}
                  placeholder="i.e. india"
                  astrike={true}
                  component={FormSelectionField}
                  validator={requiredValidator}
                  options={CountryList.map((country: any) => {
                    return {
                      value: country.ID,
                      label: country.CountryName,
                    };
                  })}
                />
              </GridLayoutItem>
              <GridLayoutItem style={{ width: "100%" }}>
                <Field
                  id={"IsActive"}
                  name={"IsActive"}
                  label={"Status"}
                  placeholder="i.e. Active"
                  component={FormSelectionField}
                  validator={requiredValidator}
                  astrike={true}
                  options={STATUSARRAY?.map((status: any) => {
                    return {
                      value: status.value,
                      label: status.label,
                    };
                  })}
                />
              </GridLayoutItem>

              <GridLayoutItem
                style={{
                  marginTop: "20px",
                  width: "100%",
                }}
              >
                <ButtonWithLoading
                  label={StateID ? "Update" : "Save"}
                  type="submit"
                  disabled={!formRenderProps.allowSubmit || formLoading}
                  loading={formLoading}
                />

                <Button
                  type="button"
                  fillMode={"outline"}
                  themeColor={"primary"}
                  style={{ marginLeft: 10 }}
                  onClick={handleFormClose}
                >
                  Cancel
                </Button>
              </GridLayoutItem>
            </GridLayout>
          </FormElement>
        )}
      />
    </>
  );
};

export default CreateState;
